import apiClient from './apiClient';
import { computeUploadChecksum } from '../../utils/uploadChecksum';

interface PresignResponse {
  blob_signed_id: string;
  upload_url: string;
  headers: Record<string, string>;
}

/**
 * Files go straight to object storage via a presigned PUT — never through a
 * service as multipart (the BFF proxy can't forward multipart bodies, and
 * uploads shouldn't transit the app tier). presign + direct-PUT + finalize,
 * mirroring the pic-upload flow. The presign/finalize calls are JSON, which
 * the BFF proxies fine; only the file bytes go cross-origin to Garage.
 *
 * `base` is the resource root (e.g. `/direct/usersvc/settings`), `kind` the
 * asset segment (`logo`, `favicon`). The finalize response — whatever shape
 * that resource returns — is the resolved value.
 */
export async function uploadAssetDirect<T>(base: string, kind: string, file: File): Promise<T> {
  const checksum = await computeUploadChecksum(file);

  const presign = await apiClient.post<PresignResponse>(`${base}/${kind}/presign`, {
    filename: file.name,
    byte_size: file.size,
    checksum,
    content_type: file.type,
  });

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presign.upload_url);
    Object.entries(presign.headers ?? {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`PUT ${xhr.status}`));
    xhr.onerror = () => reject(new Error('network error'));
    xhr.send(file);
  });

  return apiClient.post<T>(`${base}/${kind}`, { blob_signed_id: presign.blob_signed_id });
}
